/**
 * What this audience actually reaches, and what it drops.
 *
 * The reason this card exists in this shape: a campaign that says "4 000
 * companies" and then mails 3 100 has lost 900 rows that nobody can explain, and
 * the operator's only options are to trust it or to stop using it. So every
 * candidate the screening examined ends up in exactly one bucket, the buckets
 * add back up to `examined`, and a mismatch is reported as a screening bug
 * rather than smoothed over.
 *
 * One number leads: how many letters would go out if the send ran now. It is the
 * only number on this screen that anybody has to act on, and in the previous
 * version it was the fourth of four equal tiles.
 *
 * Two states this card is careful about, because both used to be silent:
 *   - STALE. The preview is debounced, so between an edit and the refetch the
 *     numbers on screen belong to the previous filter. They are dimmed and
 *     labelled instead of quietly looking current.
 *   - ZERO WITH A REASON. A filter reading a column the mirror never imported
 *     matches nothing, and "0" looks identical whether the filter is narrow or
 *     the column is empty. `deadFilters` names the culprits.
 */
import { AlertTriangle, Info, Loader2, RefreshCw } from "lucide-react";
import { Button, cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import type { AudiencePreview } from "../../../../../lib/marketing";
import { Note, Stat, num, pct } from "../../shared";
import { Funnel } from "./Funnel";
import { DropReasons } from "./DropReasons";
import { SampleList } from "./SampleList";

export function PreviewCard({
  preview,
  loading,
  stale,
  error,
  deadFilters,
  onRefresh,
}: {
  preview: AudiencePreview | null;
  loading: boolean;
  /** The filter has changed since these numbers were computed. */
  stale: boolean;
  error: string | null;
  /** Names of active filters whose backing column holds no data at all. */
  deadFilters: string[];
  onRefresh: () => void;
}) {
  const t = useT();

  return (
    <section className="rounded-2xl border border-border bg-card/70 shadow-soft backdrop-blur">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-tight">{t("marketing.preview.title")}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("marketing.preview.intro")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          {t("marketing.preview.refresh")}
        </Button>
      </header>

      <div className="space-y-4 p-5">
        {error && (
          <Note tone="bad" icon={<AlertTriangle className="size-4" />}>
            {error}
          </Note>
        )}

        {!preview && !error && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {loading ? t("marketing.preview.computing") : t("marketing.preview.none")}
          </p>
        )}

        {preview && (
          <div className={cn("space-y-4 transition-opacity", stale && "opacity-50")}>
            {stale && (
              <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                {t("marketing.preview.stale")}
              </p>
            )}

            {/* The headline. Everything else on this card explains it. */}
            <Stat
              label={t("marketing.preview.sendableNow")}
              value={num(preview.sendable)}
              sub={t("marketing.preview.sendableOf", {
                total: num(preview.total),
                pct: pct(preview.sendable, Math.max(1, preview.total)),
              })}
              tone={preview.sendable > 0 ? "good" : "warn"}
              size="lg"
            />

            {preview.sendable === 0 && deadFilters.length > 0 && (
              <Note tone="warn" icon={<AlertTriangle className="size-4" />}>
                {t("marketing.preview.zeroBecauseEmptyColumn", { filters: deadFilters.join(", ") })}
              </Note>
            )}

            <Funnel preview={preview} />

            {preview.partial && (
              <Note tone="info" icon={<Info className="size-3.5" />}>
                {t("marketing.preview.partial", { examined: num(preview.examined), total: num(preview.total) })}
              </Note>
            )}

            <div className="border-t border-border pt-4">
              <DropReasons preview={preview} />
            </div>

            {/* Reported, not filtered: both are judgement calls the screening
                deliberately leaves to a person. */}
            <div className="grid grid-cols-3 gap-2 border-t border-border pt-4">
              <Stat label={t("marketing.preview.freemailShort")} value={num(preview.freemail)} tone="muted" />
              <Stat label={t("marketing.preview.roleShort")} value={num(preview.roleAccounts)} tone="muted" />
              <Stat label={t("marketing.preview.unknownShort")} value={num(preview.unknownPersonType)} tone="muted" />
            </div>

            {/* Its own row rather than a fourth column, because it is not the
                same kind of fact as the three above. Those are judgement calls
                left to a person; this one is what the letters WILL contain, and
                it is shown even at zero - an operator wondering whether the
                notice exists must be able to see the answer rather than its
                absence. */}
            <div className="border-t border-border pt-4">
              <Stat
                label={t("marketing.preview.privacyNoticeLabel")}
                value={num(preview.needsPrivacyNotice)}
                sub={t("marketing.preview.privacyNoticeNote")}
                tone="muted"
              />
            </div>

            <div className="border-t border-border pt-4">
              <SampleList sample={preview.sample} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
